import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type {
  IssueCategory,
  OnlineEvalIssueFilters,
} from '../online-eval.types';

type DeleteIssuesDialogProps = {
  open: boolean;
  filters: OnlineEvalIssueFilters;
  categories: IssueCategory[];
  matchingCount: number;
  pending: boolean;
  error: string | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
};

const formatDateFilter = (dateTimeLocal: string): string =>
  new Date(dateTimeLocal).toLocaleString();

const describeFilters = (
  filters: OnlineEvalIssueFilters,
  categories: IssueCategory[]
): [string, string][] => {
  const categoryNames = filters.categoryIds.map(
    (categoryId) =>
      categories.find((category) => category.id === categoryId)?.name ??
      categoryId
  );
  const described: [string, string][] = [
    ['Search', filters.search],
    ['Categories', categoryNames.join(', ')],
    ['User', filters.user],
    ['Agents', filters.agents.join(', ')],
    ['Models', filters.models.join(', ')],
    [
      'Findings after',
      filters.createdAfter && formatDateFilter(filters.createdAfter),
    ],
    [
      'Findings before',
      filters.createdBefore && formatDateFilter(filters.createdBefore),
    ],
  ];
  return described.filter(([, value]) => value !== '');
};

export const DeleteIssuesDialog = ({
  open,
  filters,
  categories,
  matchingCount,
  pending,
  error,
  onOpenChange,
  onConfirm,
}: DeleteIssuesDialogProps) => {
  const describedFilters = describeFilters(filters, categories);
  const count = matchingCount.toLocaleString();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete about {count} findings?
          </DialogTitle>
          <DialogDescription>
            This permanently deletes every finding that matches the filters
            below. It cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 rounded-md border border-destructive bg-destructive/10 p-4 text-sm">
          <p className="text-3xl font-semibold text-destructive">~{count}</p>
          <p>
            This number is approximate. It was counted when the table last
            loaded. The deletion applies the filters again when it runs, so it
            also deletes matching findings flagged since then.
          </p>
          <ul className="space-y-0.5 border-t border-destructive/20 pt-2 text-xs">
            {describedFilters.map(([label, value]) => (
              <li key={label}>
                <span className="font-medium">{label}:</span> {value}
              </li>
            ))}
          </ul>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? 'Deleting…' : `Delete about ${count} findings`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
